import {
  postDeleteMessages,
  getMessages,
  markRead,
  markUnread,
  postMessage,
  postMessageWithAttachments,
  getMessagesCount,
} from '../../api/api'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { updateMeta } from './meta'
import { LOGOUT } from 'portal-modules/auth/redux/modules/user'
import dedupe from 'portal-modules/common/utils/dedupe'
import createRequestSaga from 'portal-modules/common/redux/create-request-saga'
import createFormAction from 'portal-modules/common/redux/create-form-action'

// action types
const DELETE_MESSAGES = 'rp/messages/DELETE_MESSAGES'
const MESSAGES_DELETED = 'rp/messages/MESSAGES_DELETED'
const MARK_MESSAGES_READ = 'rp/messages/MARK_MESSAGES_READ'
const MESSAGES_MARKED_READ = 'rp/messages/MESSAGES_MARKED_READ'
const MARK_MESSAGES_UNREAD = 'rp/messages/MARK_MESSAGES_UNREAD'
const MESSAGES_MARKED_UNREAD = 'rp/messages/MESSAGES_MARKED_UNREAD'
const RECEIVE_MESSAGES = 'rp/messages/RECEIVE_MESSAGES'
const REQUEST_MESSAGES = 'rp/messages/REQUEST_MESSAGES'
const REQUEST_MESSAGES_COUNT = 'rp/messages/REQUEST_MESSAGES_COUNT'
const SEND_NEW_MESSAGE = 'rp/messages/SEND_NEW_MESSAGE'

// reducer
export default (state = [], { payload, type }) => {
  switch (type) {
    case MESSAGES_DELETED:
      return state.filter(({ id }) => !payload.includes(id))
    case MESSAGES_MARKED_READ:
      return state.map(message =>
        payload.includes(message.id) ? { ...message, is_read: true } : message,
      )
    case MESSAGES_MARKED_UNREAD:
      return state.map(message =>
        payload.includes(message.id) ? { ...message, is_read: false } : message,
      )
    case RECEIVE_MESSAGES:
      return dedupe([...state, ...payload])
    case LOGOUT:
      return []
    default:
      return state
  }
}

// action creators
export const requestMessages = () => ({ type: REQUEST_MESSAGES })

export const requestMessagesCount = () => ({
  type: REQUEST_MESSAGES_COUNT,
})

// payload = [id]
export const deleteMessages = (payload, callback) => ({
  type: DELETE_MESSAGES,
  callback,
  payload,
})

// payload = [id]
export const markMessagesRead = (payload, callback) => ({
  type: MARK_MESSAGES_READ,
  callback,
  payload,
})

// payload = [id]
export const markMessagesUnread = (payload, callback) => ({
  type: MARK_MESSAGES_UNREAD,
  callback,
  payload,
})

export const sendMessage = createFormAction(SEND_NEW_MESSAGE)

// sagas
function* fetchMessages() {
  const { messageCount, total, page, page_size } = yield select(
    ({ messages, meta }) => ({
      messageCount: messages.length,
      ...(meta.messages || {}),
    }),
  )

  if (!total || messageCount < total) {
    const next = page + 1
    const { messages, total } = yield call(getMessages, {
      page_number: next,
      page_size,
    })

    if (messages) {
      yield all([
        put({ type: RECEIVE_MESSAGES, payload: messages }),
        put(
          updateMeta({
            key: 'messages',
            updates: { page: next, total: +total },
          }),
        ),
      ])
    }
  }
}

function* postDeletes({ payload }) {
  const { unread_msg_count } = yield call(postDeleteMessages, {
    message_ids: payload,
  })

  yield all([
    put({ type: MESSAGES_DELETED, payload }),
    put(
      updateMeta({
        key: 'messageCounts',
        updates: { messages: +unread_msg_count },
      }),
    ),
  ])

  const totalMessageCount = yield select(({ messages }) => messages.length)

  yield put(
    updateMeta({
      key: 'messages',
      updates: { total: totalMessageCount },
    }),
  )
}

function* postUpdates({ payload, type }) {
  const method = type === MARK_MESSAGES_READ ? markRead : markUnread
  const action =
    type === MARK_MESSAGES_READ ? MESSAGES_MARKED_READ : MESSAGES_MARKED_UNREAD

  const { unread_msg_count } = yield call(method, { message_ids: payload })

  yield all([
    put({ type: action, payload }),
    put(
      updateMeta({
        key: 'messageCounts',
        updates: { messages: +unread_msg_count },
      }),
    ),
  ])
}

function* fetchMessagesCount() {
  const data = yield call(getMessagesCount, { include_alerts: true })
  yield put(
    updateMeta({
      key: 'messageCounts',
      // make sure everything is a number
      updates: Object.entries(data).reduce(
        (obj, [key, val]) => ({
          ...obj,
          [key]: +val,
        }),
        {},
      ),
    }),
  )
}

function* watchRequestMessages() {
  yield takeLatest(
    REQUEST_MESSAGES,
    createRequestSaga(fetchMessages, {
      key: 'messages',
      message: __('Checking for messages'),
    }),
  )
}

function* watchDeleteMessages() {
  yield takeLatest(
    DELETE_MESSAGES,
    createRequestSaga(postDeletes, {
      key: 'deleteMessages',
      message: __('Deleting message(s)'),
      successMessage: __('Message(s) deleted'),
      write: true,
    }),
  )
}

function* watchUpdateMessages() {
  yield takeLatest(
    [MARK_MESSAGES_READ, MARK_MESSAGES_UNREAD],
    createRequestSaga(postUpdates, {
      key: 'updateMessages',
      message: __('Syncing updates'),
      successMessage: __('Messages updated'),
      write: true,
    }),
  )
}

function* watchRequestMessagesCount() {
  yield takeLatest(
    [REQUEST_MESSAGES_COUNT],
    createRequestSaga(fetchMessagesCount, {
      key: 'messages',
      message: __('Checking for messages'),
    }),
  )
}

/**
 * Saga handler for sending new messages with optional file attachments
 * Automatically detects if files are present and uses appropriate endpoint (multipart vs JSON)
 * @param {Object} action - Redux action
 * @param {Object} action.payload - Message data including optional attachments array
 * @param {Function} action.onError - Error callback function
 */
function* postNewMessage({ payload, onError }) {
  try {
    // If payload has attachments (files), use FormData and multipart endpoint
    // Otherwise use regular JSON endpoint
    // Files must be File instances to be included in FormData
    const validFiles = payload.attachments && Array.isArray(payload.attachments) 
      ? payload.attachments.filter(file => file instanceof File || (file && typeof file === 'object' && file.name))
      : []
    
    if (validFiles.length > 0) {
      // Create FormData for multipart/form-data request
      // This is required when uploading files
      const formData = new FormData()
      
      // Append all non-file fields to FormData
      // Booleans are converted to '1'/'0' strings as FormData doesn't handle booleans natively
      Object.keys(payload).forEach(key => {
        if (key !== 'attachments' && payload[key] !== undefined && payload[key] !== null) {
          const value = payload[key]
          // Convert boolean to string for FormData compatibility
          if (typeof value === 'boolean') {
            formData.append(key, value ? '1' : '0')
          } else {
            formData.append(key, value)
          }
        }
      })
      
      // Append each file to FormData with indexed key format: attachments[0], attachments[1], etc.
      // This format allows backend to receive files as an array
      validFiles.forEach((file, index) => {
        if (file instanceof File) {
          formData.append(`attachments[${index}]`, file)
        }
      })
      
      // Use multipart endpoint for file uploads
      yield call(postMessageWithAttachments, formData)
    } else {
      // Remove attachments from payload if it's empty/undefined
      const { attachments, ...dataWithoutAttachments } = payload
      yield call(postMessage, dataWithoutAttachments)
    }
  } catch (error) {
    // Handle errors in file upload or API call
    if (onError && typeof onError === 'function') {
      onError(error.message || __('Failed to send message. Please try again.'))
    }
    throw error
  }
}

function* watchSendMessage() {
  yield takeLatest(
    SEND_NEW_MESSAGE,
    createRequestSaga(postNewMessage, {
      key: 'sendMessages',
      message: __('Sending your message'),
      successMessage: __('Message successfully sent'),
      write: true,
    }),
  )
}

export const sagas = [
  watchRequestMessages,
  watchDeleteMessages,
  watchUpdateMessages,
  watchRequestMessagesCount,
  watchSendMessage,
]
